import React, { useState } from 'react';
import { UserStatistics } from '../../../types/user';
import styles from '../AdminPanel.module.css';
import { MdPerson, MdShowChart, MdDateRange } from 'react-icons/md';

interface AnalyticsProps {
  stats: UserStatistics | null;
}

const Analytics: React.FC<AnalyticsProps> = ({ stats }) => {
  const [timeRange, setTimeRange] = useState('last30days');
  
  if (!stats) {
    return <div>Yükleniyor...</div>;
  }
  
  // Burada gerçek veriler olmadığı için örnek grafikler için kullanacağımız demoları oluşturalım
  const generateRandomData = (days: number) => {
    const data = [];
    const now = new Date();
    for (let i = days; i >= 0; i--) {
      const date = new Date();
      date.setDate(now.getDate() - i);
      data.push({
        date: date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }),
        activeUsers: Math.round(Math.random() * 50 + 200),
        newUsers: Math.round(Math.random() * 30),
        premiumConversions: Math.round(Math.random() * 5)
      });
    }
    return data;
  };
  
  const getDaysForRange = () => {
    switch (timeRange) {
      case 'last7days': return 7;
      case 'last30days': return 30;
      case 'last90days': return 90;
      default: return 30;
    }
  };
  
  const analyticsData = generateRandomData(getDaysForRange());
  
  // Platform dağılımı için demo veriler
  const platformData = [
    { name: 'Android', value: 45 },
    { name: 'iOS', value: 35 },
    { name: 'Web', value: 20 }
  ];
  
  // Şehir dağılımı için demo veriler
  const cityData = [
    { name: 'İstanbul', value: 40 },
    { name: 'Ankara', value: 25 },
    { name: 'İzmir', value: 20 },
    { name: 'Diğer', value: 15 }
  ];
  
  return (
    <div>
      <h2>Analitik & İstatistikler</h2>
      
      <div className={styles.timeRangeSelector}>
        <button 
          className={`${styles.timeRangeButton} ${timeRange === 'last7days' ? styles.activeTimeRange : ''}`}
          onClick={() => setTimeRange('last7days')}
        >
          Son 7 Gün
        </button>
        <button 
          className={`${styles.timeRangeButton} ${timeRange === 'last30days' ? styles.activeTimeRange : ''}`}
          onClick={() => setTimeRange('last30days')}
        >
          Son 30 Gün
        </button>
        <button 
          className={`${styles.timeRangeButton} ${timeRange === 'last90days' ? styles.activeTimeRange : ''}`}
          onClick={() => setTimeRange('last90days')}
        >
          Son 90 Gün
        </button>
      </div>
      
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}><MdPerson /></div>
          <div className={styles.statValue}>{stats.totalUsers}</div>
          <div className={styles.statLabel}>Toplam Kullanıcı</div>
        </div>
        
        <div className={styles.statCard}>
          <div className={styles.statIcon}><MdShowChart /></div>
          <div className={styles.statValue}>{stats.activeUsers}</div>
          <div className={styles.statLabel}>Aktif Kullanıcı</div>
        </div>
        
        <div className={styles.statCard}>
          <div className={styles.statIcon}><MdDateRange /></div>
          <div className={styles.statValue}>{stats.newUsersLast30Days}</div>
          <div className={styles.statLabel}>Yeni Kullanıcı (30 Gün)</div>
        </div>
        
        <div className={styles.statCard}>
          <div className={styles.statValue}>{Math.round(stats.premiumUsers / stats.totalUsers * 100)}%</div>
          <div className={styles.statLabel}>Premium Oran</div>
        </div>
      </div>
      
      <div className={styles.cardSection}>
        <h3>Kullanıcı Aktivitesi</h3>
        <div className={styles.chartContainer}>
          <div className={styles.chart}>
            <div className={styles.chartLegend}>
              <div className={styles.legendItem}>
                <div className={styles.legendColor} style={{ background: 'var(--primary-color)' }}></div>
                <span>Aktif Kullanıcılar</span>
              </div>
              <div className={styles.legendItem}>
                <div className={styles.legendColor} style={{ background: 'var(--secondary-color)' }}></div>
                <span>Yeni Kullanıcılar</span>
              </div>
              <div className={styles.legendItem}>
                <div className={styles.legendColor} style={{ background: 'var(--accent-color)' }}></div>
                <span>Premium Dönüşümler</span>
              </div>
            </div>
            
            <div className={styles.lineChart}>
              {analyticsData.map((day, index) => (
                <div key={index} className={styles.chartDay}>
                  <div className={styles.chartBars}>
                    <div 
                      className={styles.activeUserBar} 
                      style={{ height: `${(day.activeUsers / 300) * 100}%` }}
                      title={`Aktif Kullanıcı: ${day.activeUsers}`}
                    ></div>
                    <div 
                      className={styles.newUserBar} 
                      style={{ height: `${(day.newUsers / 50) * 100}%` }}
                      title={`Yeni Kullanıcı: ${day.newUsers}`}
                    ></div>
                    <div 
                      className={styles.premiumBar} 
                      style={{ height: `${(day.premiumConversions / 10) * 100}%` }}
                      title={`Premium Dönüşüm: ${day.premiumConversions}`}
                    ></div>
                  </div>
                  <div className={styles.chartDayLabel}>{day.date}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      <div className={styles.analyticsSections}>
        <div className={styles.cardSection}>
          <h3>Platform Dağılımı</h3>
          <div className={styles.donutChartContainer}>
            <div className={styles.donutChart}>
              {platformData.map((item, index) => (
                <div 
                  key={index} 
                  className={styles.donutSegment}
                  style={{ 
                    '--percentage': `${item.value}%`,
                    '--offset': `${index > 0 ? platformData.slice(0, index).reduce((acc, curr) => acc + curr.value, 0) : 0}%`,
                    '--color': `${index === 0 ? 'var(--primary-color)' : index === 1 ? 'var(--secondary-color)' : 'var(--accent-color)'}`,
                  } as React.CSSProperties}
                ></div>
              ))}
              <div className={styles.donutText}>
                <div>Platform</div>
                <div>Dağılımı</div>
              </div>
            </div>
            
            <div className={styles.chartLegend}>
              {platformData.map((item, index) => (
                <div key={index} className={styles.legendItem}>
                  <div 
                    className={styles.legendColor} 
                    style={{ 
                      background: index === 0 ? 'var(--primary-color)' : index === 1 ? 'var(--secondary-color)' : 'var(--accent-color)'
                    }}
                  ></div>
                  <span>{item.name}: %{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className={styles.cardSection}>
          <h3>Şehir Dağılımı</h3>
          <div className={styles.barChartHorizontal}>
            {cityData.map((item, index) => (
              <div key={index} className={styles.horizontalBarItem}>
                <div className={styles.horizontalBarLabel}>{item.name}</div>
                <div className={styles.horizontalBarContainer}>
                  <div 
                    className={styles.horizontalBar}
                    style={{ width: `${item.value}%` }}
                  ></div>
                  <span className={styles.horizontalBarValue}>{item.value}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className={styles.cardSection}>
        <h3>Kullanıcı Büyüme Özeti</h3>
        <div className={styles.growthMetrics}>
          <div className={styles.metricItem}>
            <div className={styles.metricLabel}>Günlük Yeni Kullanıcı</div>
            <div className={styles.metricValue}>+{Math.round(stats.newUsersLast30Days / 30)}</div>
            <div className={`${styles.metricTrend} ${styles.positive}`}>+5%</div>
          </div>
          
          <div className={styles.metricItem}>
            <div className={styles.metricLabel}>Haftalık Büyüme</div>
            <div className={styles.metricValue}>+{Math.round(stats.newUsersLast30Days / 4)}</div>
            <div className={`${styles.metricTrend} ${styles.positive}`}>+3%</div>
          </div>
          
          <div className={styles.metricItem}>
            <div className={styles.metricLabel}>Aylık Büyüme</div>
            <div className={styles.metricValue}>+{stats.newUsersLast30Days}</div>
            <div className={`${styles.metricTrend} ${styles.positive}`}>+8%</div>
          </div>
          
          <div className={styles.metricItem}>
            <div className={styles.metricLabel}>Kullanıcı Kaybı</div>
            <div className={styles.metricValue}>{Math.round(stats.totalUsers * 0.02)}</div>
            <div className={`${styles.metricTrend} ${styles.negative}`}>+1%</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
